import styles from "../../../../styles/pages/Lead/LeadManagement/PaymentsPage.module.css";
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useOutletContext } from "react-router-dom";
import { fetchLeadPayments } from "../../../../utils/api/Lead/Payments/fetchLeadPayments";
import { handleError } from "../../../../utils/custom/errorHandler";
import { AddPaymentModal } from "../../../../components/Modal/Payments/AddPaymentsModal";
import { EditPaymentModal } from "../../../../components/Modal/Payments/EditPaymentsModal";
import { AddButton } from "../../../../components/UI/Custom/AddButton";
import { formatDateTime } from "../../../../utils/custom/formatters";
import { LangContext } from '../../../../context/LangContext';
import { ThemeContext } from '../../../../context/ThemeContext';
import { SettingsModal } from "../../../../components/Modal/Payments/SettingsModal";
import { getTransactionActionLabel } from '../../../../utils/transactionActionLabels';


import DoneIcon from '@mui/icons-material/Done';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import SettingsIcon from '@mui/icons-material/Settings';

const translations = {
    ru: {
        loading: "Загрузка данных о платежах...",
        loadingError: "Ошибка загрузки платежей...",
        addTransaction: "Добавить транзакцию",
        settings: "Настройки",
        editClickTitle: "Нажмите для редактирования",
        adminData: "Данные администратора",
        adminId: "ID",
        adminFirstName: "Имя",
        adminLastName: "Фамилия",
        adminEmail: "Email",
        adminRole: "Роль",
        adminStatus: "Статус",
        adminCreated: "Создан",
        previousPage: "Назад",
        nextPage: "Вперед",
        tableHeaders: {
            id: "ID",
            platformId: "ID Платформы",
            action: "Действие",
            asset: "Актив",
            amount: "Сумма",
            date: "Дата",
            status: "Статус",
            leadId: "ID Лида",
            bank: "Банк",
            wallet: "Кошелек",
            number: "Номер",
            comment: "Комментарий",
            createdByAdmin: "Создано админом",
            createdAt: "Создано в",
            updatedAt: "Обновлено в",
        },
        noPayments: "Платежей не найдено.",
        statuses: {
            success: "Успешно",
            process: "В процессе",
            fail: "Отказ",
            canceled: "Отменено",
            waitaccept: "Ожидает подтверждения",
        }
    },
    en: {
        loading: "Loading payment data...",
        loadingError: "Error loading payments...",
        addTransaction: "Add transaction",
        settings: "Settings",
        editClickTitle: "Click to edit",
        adminData: "Administrator Data",
        adminId: "ID",
        adminFirstName: "First Name",
        adminLastName: "Last Name",
        adminEmail: "Email",
        adminRole: "Role",
        adminStatus: "Status",
        adminCreated: "Created",
        previousPage: "Previous",
        nextPage: "Next",
        tableHeaders: {
            id: "ID",
            platformId: "Platform ID",
            action: "Action",
            asset: "Asset",
            amount: "Amount",
            date: "Date",
            status: "Status",
            leadId: "Lead ID",
            bank: "Bank",
            wallet: "Wallet",
            number: "Number",
            comment: "Comment",
            createdByAdmin: "Created by Admin",
            createdAt: "Created At",
            updatedAt: "Updated At",
        },
        noPayments: "No payments found.",
        statuses: {
            success: "Success",
            process: "In Process",
            fail: "Failed",
            canceled: "Canceled",
            waitaccept: "Pending",
        }
    },
};

export const PaymentsPage = () => {
    const { id: leadId } = useParams();
    const { refreshPanelData } = useOutletContext();
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoveredAdmin, setHoveredAdmin] = useState(null);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
    const [shouldUpdatePayments, setShouldUpdatePayments] = useState(false);
    const [isEditPaymentModalOpen, setIsEditPaymentModalOpen] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [highlightedStatuses, setHighlightedStatuses] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        const storedSettings = localStorage.getItem('highlightedStatuses');
        if (storedSettings) {
            try {
                setHighlightedStatuses(JSON.parse(storedSettings));
            } catch (error) {
            }
        }
    }, []);

    const loadPayments = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, meta } = await fetchLeadPayments(leadId, currentPage, perPage);
            setPayments(data ?? []);
            setTotalPages(meta.total_pages);
        } catch (err) {
            handleError(err.response?.data || err, setError);
        } finally {
            setLoading(false);
        }
    }, [leadId, currentPage, perPage]);

    useEffect(() => {
        loadPayments();
        if (shouldUpdatePayments) {
            setShouldUpdatePayments(false);
        }
    }, [loadPayments, shouldUpdatePayments]);

    const handleMouseEnter = (event, adminData) => {
        setHoveredAdmin(adminData);
        setModalPosition({ x: event.clientX - 100, y: event.clientY + 15 });
    };

    const handleMouseLeave = () => {
        setHoveredAdmin(null);
    };

    const handlePaymentCreated = () => {
        setIsAddPaymentModalOpen(false);
        setShouldUpdatePayments(true);
        setCurrentPage(1);
        refreshPanelData();
    };

    const handleEditPaymentClick = (paymentId) => {
        setSelectedPaymentId(paymentId);
        setIsEditPaymentModalOpen(true);
    };

    const handlePaymentActionSuccess = () => {
        setIsEditPaymentModalOpen(false);
        setShouldUpdatePayments(true);
        refreshPanelData();
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleSettingsSave = (statuses) => {
        setHighlightedStatuses(statuses);
    };

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.paginationButton} ${i === currentPage ? styles.active : ''} ${theme === 'dark' ? styles.darkPaginationButton : ''} ${i === currentPage ? styles.darkActivePaginationButton : ''}`}
                    onClick={() => handlePageChange(i)}
                    disabled={loading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const getStatusClassName = (status) => {
        switch (status) {
            case 'success':
                return styles.statusSuccess;
            case 'process':
                return styles.statusProcess;
            case 'fail':
                return styles.statusFail;
            case 'canceled':
                return styles.statusCanceled;
            case 'waitaccept':
                return styles.statusWaitAccept;
            default:
                return '';
        }
    };

    const getActionClassName = (action) => {
        switch (action) {
            case 'deposit':
                return styles.actionDeposit;
            case 'withdraw':
                return styles.actionWithdraw;
            default:
                return '';
        }
    };

    const getHighlightClassName = (status) => {
        if (highlightedStatuses.includes(status)) {
            switch (status) {
                case 'success':
                    return `${styles.highlightSuccess} ${theme === 'dark' ? styles.darkHighlightSuccess : ''}`;
                case 'process':
                    return `${styles.highlightProcess} ${theme === 'dark' ? styles.darkHighlightProcess : ''}`;
                case 'fail':
                    return `${styles.highlightFail} ${theme === 'dark' ? styles.darkHighlightFail : ''}`;
                case 'canceled':
                    return `${styles.highlightCanceled} ${theme === 'dark' ? styles.darkHighlightCanceled : ''}`;
                case 'waitaccept':
                    return `${styles.highlightWaitAccept} ${theme === 'dark' ? styles.darkHighlightWaitAccept : ''}`;
                default:
                    return '';
            }
        }
        return '';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return DoneIcon;
            case 'process':
                return HourglassEmptyIcon;
            case 'fail':
                return CancelIcon;
            case 'canceled':
                return BlockIcon;
            case 'waitaccept':
                return HourglassTopIcon;
            default:
                return null;
        }
    };

    const containerClass = `${styles.container} ${theme === 'dark' ? styles.darkContainer : ''}`;
    const tableClass = `${styles.table} ${theme === 'dark' ? styles.darkTable : ''}`;
    const loadingContainerClass = `${styles.loadingContainer} ${theme === 'dark' ? styles.darkLoadingContainer : ''}`;
    const errorMessageClass = `${styles.errorMessage} ${theme === 'dark' ? styles.darkErrorMessage : ''}`;
    const paginationClass = `${styles.pagination} ${theme === 'dark' ? styles.darkPagination : ''}`;
    const settingsButtonClass = `${styles.settingsButton} ${theme === 'dark' ? styles.darkSettingsButton : ''}`;
    const adminModalClass = `${styles.adminModal} ${theme === 'dark' ? styles.darkAdminModal : ''}`;

    if (loading) {
        return <div className={loadingContainerClass}>{currentTranslation.loading}</div>;
    }

    if (error) {
        return (
            <>
                <div className={errorMessageClass}>
                    <p>{currentTranslation.loadingError}</p>
                    <p>{error.general}</p>
                </div>
            </>
        );
    }

    return (
        <div className={containerClass}>
            <div className={styles.topBar}>
                <AddButton
                    text={currentTranslation.addTransaction}
                    onClick={() => setIsAddPaymentModalOpen(true)}
                />
                <button className={settingsButtonClass} onClick={() => setIsSettingsModalOpen(true)}>
                    <SettingsIcon />
                    {currentTranslation.settings}
                </button>
            </div>

            <table className={tableClass}>
                <thead>
                    <tr>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.id}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.platformId}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.action}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.asset}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.amount}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.date}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.status}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.leadId}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.bank}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.wallet}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.number}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.comment}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.createdByAdmin}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.createdAt}</th>
                        <th className={theme === 'dark' ? styles.darkTableHeader : ''}>{currentTranslation.tableHeaders.updatedAt}</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.length === 0 ? (
                        <tr>
                            <td colSpan="15" className={`${styles.noPaymentsMessage} ${theme === 'dark' ? styles.darkNoPaymentsMessage : ''}`}>
                                {currentTranslation.noPayments}
                            </td>
                        </tr>
                    ) : (
                        payments.map((payment) => (
                            <tr key={payment.id} className={`${getHighlightClassName(payment.status)} ${theme === 'dark' ? styles.darkTableRow : ''}`}>
                                <td
                                    className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`}
                                    onClick={() => handleEditPaymentClick(payment.id)}
                                    style={{ cursor: 'pointer', color: theme === 'dark' ? '#938fff' : '#007bff', fontWeight: 'bold' }}
                                    title={currentTranslation.editClickTitle}
                                    data-label={currentTranslation.tableHeaders.id}
                                >
                                    {payment.id}
                                </td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.platformId}>{payment.platform_id}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.action}>
                                    <p className={`${getActionClassName(payment.action)} ${theme === 'dark' ? styles.darkActionBadge : ''}`}>
                                        {getTransactionActionLabel(payment.action, langSelected)}
                                    </p>
                                </td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.asset}>{payment.assets.toUpperCase()}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.amount}>{payment.amount}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.date}>{formatDateTime(payment.date)}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.status}>
                                    <div className={`${getStatusClassName(payment.status)} ${theme === 'dark' ? styles.darkStatusBadge : ''}`}>
                                        {getStatusIcon(payment.status) && (
                                            React.createElement(getStatusIcon(payment.status), { style: { fontSize: '14px' } })
                                        )}
                                        {currentTranslation.statuses[payment.status] || payment.status || '-'}
                                    </div>
                                </td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.leadId}>{payment.lead_id}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.bank}>{payment.bank || '-'}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.wallet}>{payment.wallet || '-'}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.number}>{payment.number || '-'}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.comment}>{payment.comment || '-'}</td>
                                <td
                                    className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`}
                                    onMouseEnter={(e) => handleMouseEnter(e, payment.created_by_admin)}
                                    onMouseLeave={handleMouseLeave}
                                    data-label={currentTranslation.tableHeaders.createdByAdmin}
                                >
                                    {payment.created_by_admin ? `${payment.created_by_admin.email}` : '-'}
                                </td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.createdAt}>{formatDateTime(payment.created_at)}</td>
                                <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} data-label={currentTranslation.tableHeaders.updatedAt}>{formatDateTime(payment.updated_at)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className={paginationClass}>
                    <button
                        type="button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    >
                        {currentTranslation.previousPage}
                    </button>
                    {renderPaginationButtons()}
                    <button
                        type="button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                        className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    >
                        {currentTranslation.nextPage}
                    </button>
                </div>
            )}

            {hoveredAdmin && (
                <div
                    className={adminModalClass}
                    style={{ left: modalPosition.x, top: modalPosition.y }}
                >
                    <h4>{currentTranslation.adminData}</h4>
                    <p><strong>{currentTranslation.adminId}:</strong> {hoveredAdmin.id}</p>
                    <p><strong>{currentTranslation.adminFirstName}:</strong> {hoveredAdmin.first_name || '-'}</p>
                    <p><strong>{currentTranslation.adminLastName}:</strong> {hoveredAdmin.last_name || '-'}</p>
                    <p><strong>{currentTranslation.adminEmail}:</strong> {hoveredAdmin.email}</p>
                    <p><strong>{currentTranslation.adminRole}:</strong> {hoveredAdmin.role?.name || '-'}</p>
                    <p><strong>{currentTranslation.adminStatus}:</strong> {hoveredAdmin.status}</p>
                    <p><strong>{currentTranslation.adminCreated}:</strong> {formatDateTime(hoveredAdmin.created_at)}</p>
                </div>
            )}

            {isAddPaymentModalOpen && (
                <AddPaymentModal
                    leadId={leadId}
                    onClose={() => setIsAddPaymentModalOpen(false)}
                    onPaymentCreated={handlePaymentCreated}
                />
            )}

            {isEditPaymentModalOpen && selectedPaymentId && (
                <EditPaymentModal
                    leadId={leadId}
                    paymentId={selectedPaymentId}
                    onClose={() => setIsEditPaymentModalOpen(false)}
                    onPaymentActionSuccess={handlePaymentActionSuccess}
                />
            )}

            {isSettingsModalOpen && (
                <SettingsModal
                    onClose={() => setIsSettingsModalOpen(false)}
                    onSave={handleSettingsSave}
                />
            )}
        </div>
    );
};
